import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CirclePause, ShoppingCart, Trash2, UserPlus } from 'lucide-react';
import { ORDER_TYPES } from '@/utils/constants';
import { formatCurrency } from '@/utils/formatters';
import { Alert, Button, IconButton, SegmentedControl } from '@/components/ui';
import { useCartController } from './useCartController';
import { CartLine } from './CartLine';
import { CartTotals } from './CartTotals';
import { DiscountField } from './DiscountField';
import { ClearCartSheet, CustomerSheet, HoldCartSheet } from './CartSheets';

/** 04 right column (lg only): the pinned cart beside the catalog. Owns the calculate effect. */
export const CartPanel: React.FC<{ className?: string }> = ({ className }) => {
    const { t } = useTranslation();
    const cart = useCartController();
    const [sheet, setSheet] = useState<'hold' | 'clear' | 'customer' | null>(null);
    const hasCustomer = !!(cart.customerName || cart.customerPhone || cart.notes);

    return (
        <aside className={clsx('w-[400px] shrink-0 flex-col border-l border-line-subtle bg-surface-card p-[22px]', className)}>
            <div className="flex items-center gap-2">
                <h2 className="flex-1 text-xl font-black">
                    {t('cart.newOrder')} {cart.itemCount > 0 && <span className="text-ink-muted">{cart.itemCount}</span>}
                </h2>
                {cart.items.length > 0 && (
                    <>
                        <button type="button" onClick={() => setSheet('hold')} className="flex h-[38px] items-center gap-1.5 rounded-pill bg-surface-sunken px-3.5 text-[13px] font-extrabold text-ink-brand hover:bg-primary-100">
                            <CirclePause className="h-4 w-4" />
                            {t('cart.hold')}
                        </button>
                        <IconButton variant="sunken" size="sm" label={t('cart.clearCart')} onClick={() => setSheet('clear')} className="!text-status-danger">
                            <Trash2 />
                        </IconButton>
                    </>
                )}
            </div>

            <div className="pt-3.5">
                <SegmentedControl
                    fullWidth
                    value={cart.orderType}
                    onChange={cart.setOrderType}
                    options={ORDER_TYPES.map((v) => ({ value: v, label: t(`cart.orderTypes.${v}` as 'cart.orderTypes.DINE_IN') }))}
                />
            </div>

            <button
                type="button"
                onClick={() => setSheet('customer')}
                className={clsx(
                    'mt-3 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-left text-[13px] font-bold',
                    hasCustomer ? 'bg-brandsoft text-ink-brand' : 'border border-dashed border-line text-ink-secondary',
                )}
            >
                <UserPlus className="h-[17px] w-[17px] shrink-0 text-ink-brand" />
                <span className="min-w-0 flex-1 truncate">{hasCustomer ? [cart.customerName, cart.customerPhone, cart.notes].filter(Boolean).join(' · ') : t('cart.addCustomer')}</span>
                {!hasCustomer && <span className="font-semibold text-ink-muted">{cart.requireCustomerInfo ? '*' : t('cart.optional')}</span>}
            </button>

            {cart.error && <Alert variant="error" message={cart.error} className="mt-3" />}

            <div className="mt-1.5 min-h-0 flex-1 overflow-y-auto">
                {cart.items.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <ShoppingCart className="h-10 w-10 text-primary-200" />
                        <p className="text-sm font-bold">{t('cart.empty')}</p>
                        <p className="text-xs">{t('cart.emptySubtitle')}</p>
                    </div>
                ) : (
                    cart.items.map((item) => (
                        <CartLine
                            key={item.compositeId}
                            item={item}
                            showThumb={false}
                            stepperSize="sm"
                            className="border-b border-line-subtle"
                            onQty={(q) => cart.setQty(item.compositeId, q)}
                            onNotes={(n) => cart.setItemNotes(item.compositeId, n)}
                            onRemove={() => cart.remove(item.compositeId)}
                        />
                    ))
                )}
            </div>

            {cart.items.length > 0 && (
                <div className="flex flex-col gap-3 pt-3">
                    <DiscountField cart={cart} />
                    <CartTotals calculation={cart.calculation} loading={cart.isCalculating} large />
                    <Button variant="primary" size="lg" fullWidth onClick={cart.checkout} disabled={!cart.canCheckout} loading={cart.isCalculating && !cart.calculation}>
                        {cart.calculation ? t('cart.payCta', { amount: formatCurrency(cart.calculation.grandTotal) }) : t('cart.proceedToCheckout')}
                    </Button>
                </div>
            )}

            <HoldCartSheet cart={cart} isOpen={sheet === 'hold'} onClose={() => setSheet(null)} />
            <ClearCartSheet cart={cart} isOpen={sheet === 'clear'} onClose={() => setSheet(null)} />
            <CustomerSheet cart={cart} isOpen={sheet === 'customer'} onClose={() => setSheet(null)} />
        </aside>
    );
};
export default CartPanel;
